import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate, useLocation } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function NovaConsultaPage({ user, onLogout, showConfig }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [pacientes, setPacientes] = useState([]);
  const [procedimentos, setProcedimentos] = useState([]);
  const [loading, setLoading] = useState(false);
  const editingConsulta = location.state?.consulta || null;
  
  const [formData, setFormData] = useState({
    paciente_id: '',
    data_consulta: '',
    hora_inicio: '',
    hora_fim: '',
    categoria: '',
    valor: '',
    observacoes: '',
    modo_atendimento: ''
  });

  useEffect(() => {
    loadPacientes();
    loadProcedimentos();
    if (editingConsulta) {
      setFormData({
        paciente_id: editingConsulta.paciente_id || '',
        data_consulta: editingConsulta.data_consulta || '',
        hora_inicio: editingConsulta.hora_inicio || '',
        hora_fim: editingConsulta.hora_fim || '',
        categoria: editingConsulta.categoria || '',
        valor: editingConsulta.valor || '',
        observacoes: editingConsulta.observacoes || '',
        modo_atendimento: editingConsulta.modo_atendimento || ''
      });
    }
  }, [editingConsulta]);

  const loadPacientes = async () => {
    try {
      const response = await axios.get(`${API}/pacientes`);
      // Ordenar pacientes por nome (ordem alfabética)
      const sortedPacientes = response.data.sort((a, b) => {
        const nomeA = (a.nome_completo || '').toLowerCase();
        const nomeB = (b.nome_completo || '').toLowerCase();
        return nomeA.localeCompare(nomeB, 'pt-BR');
      });
      setPacientes(sortedPacientes);
    } catch (error) {
      console.error('Erro ao carregar pacientes:', error);
      toast.error('Erro ao carregar pacientes');
    }
  };

  const loadProcedimentos = async () => {
    try {
      const response = await axios.get(`${API}/procedimentos`);
      setProcedimentos(response.data);
    } catch (error) {
      console.error('Erro ao carregar procedimentos:', error);
    }
  };

  // Ao selecionar procedimento, atualiza o valor padrão
  const handleProcedimentoChange = (value) => {
    const proc = procedimentos.find(p => p.nome === value);
    setFormData(prev => ({
      ...prev,
      categoria: value,
      valor: proc?.valor_padrao || prev.valor
    }));
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Prepare data with valor as number (handle both . and , as decimal separator)
      const valorStr = formData.valor ? formData.valor.toString().replace(',', '.') : '';
      const dataToSend = {
        data_consulta: formData.data_consulta,
        hora_inicio: formData.hora_inicio,
        hora_fim: formData.hora_fim,
        categoria: formData.categoria,
        valor: valorStr ? parseFloat(valorStr) : null,
        observacoes: formData.observacoes,
        modo_atendimento: formData.modo_atendimento || null
      };
      
      // Only include paciente_id for new consultas
      if (!editingConsulta && formData.paciente_id) {
        dataToSend.paciente_id = formData.paciente_id;
      }

      if (editingConsulta) {
        const response = await axios.put(`${API}/consultas/${editingConsulta.id}`, dataToSend);
        navigate(`/agenda?data=${formData.data_consulta}`, { 
          state: { toast: 'Consulta atualizada com sucesso!', updatedConsulta: response.data } 
        });
      } else {
        const response = await axios.post(`${API}/consultas`, dataToSend);
        navigate(`/agenda?data=${formData.data_consulta}`, { 
          state: { toast: 'Consulta agendada com sucesso!', newConsulta: response.data } 
        });
      }
    } catch (error) {
      console.error('Erro ao salvar consulta:', error);
      
      // Tratamento de erro melhorado
      let errorMessage = 'Erro ao salvar consulta';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        
        // Se detail for uma string, usar diretamente
        if (typeof detail === 'string') {
          errorMessage = detail;
        }
        // Se detail for um array de erros de validação (Pydantic)
        else if (Array.isArray(detail)) {
          errorMessage = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
        }
        // Se detail for um objeto
        else if (typeof detail === 'object') {
          errorMessage = detail.msg || JSON.stringify(detail);
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout user={user} onLogout={onLogout} showConfig={showConfig}>
      <div className="max-w-3xl mx-auto" data-testid="nova-consulta-page">
        <div className="mb-6">
          <button
            onClick={() => navigate('/agenda')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
            data-testid="btn-voltar"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Voltar para Agenda
          </button>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
            {editingConsulta ? 'Editar Consulta' : 'Agendar Nova Consulta'}
          </h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">Preencha os dados para {editingConsulta ? 'atualizar' : 'agendar'} a consulta</p>
        </div>

        <Card className="p-4 sm:p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="paciente_id">Paciente {!editingConsulta && '*'}</Label>
              {editingConsulta ? (
                <>
                  <Input
                    value={editingConsulta.paciente_nome || 'Paciente não vinculado'}
                    disabled
                    className="bg-gray-100 cursor-not-allowed"
                    data-testid="input-paciente-nome"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Não é possível alterar o paciente de uma consulta existente.
                  </p>
                </>
              ) : (
                <select
                  id="paciente_id"
                  name="paciente_id"
                  value={formData.paciente_id}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
                  data-testid="select-paciente"
                >
                  <option value="">Selecione um paciente</option>
                  {pacientes.map((paciente) => (
                    <option key={paciente.id} value={paciente.id}>
                      {paciente.nome_completo}
                    </option>
                  ))}
                </select>
              )}
              {!editingConsulta && pacientes.length === 0 && (
                <p className="text-sm text-amber-600 mt-1">
                  Nenhum paciente cadastrado.{' '}
                  <button
                    type="button"
                    onClick={() => navigate('/pacientes')}
                    className="underline hover:no-underline"
                  >
                    Cadastrar paciente primeiro
                  </button>
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <div className="space-y-2">
                <Label htmlFor="data_consulta">Data da Consulta *</Label>
                <Input
                  id="data_consulta"
                  name="data_consulta"
                  type="date"
                  value={formData.data_consulta}
                  onChange={handleChange}
                  required
                  data-testid="input-data"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="categoria">Procedimento *</Label>
                <select
                  id="categoria"
                  name="categoria"
                  value={formData.categoria}
                  onChange={(e) => handleProcedimentoChange(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
                  data-testid="select-categoria"
                >
                  <option value="">Selecione um procedimento</option>
                  {procedimentos.length > 0 ? (
                    procedimentos.map((proc) => (
                      <option key={proc.id} value={proc.nome}>
                        {proc.nome}
                      </option>
                    ))
                  ) : (
                    <>
                      <option value="Implanon">Implanon</option>
                      <option value="DIU">DIU</option>
                      <option value="Consulta Clínica">Consulta Clínica</option>
                      <option value="Reposição Hormonal">Reposição Hormonal</option>
                    </>
                  )}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <div className="space-y-2">
                <Label htmlFor="hora_inicio">Horário de Início *</Label>
                <Input
                  id="hora_inicio"
                  name="hora_inicio"
                  type="time"
                  value={formData.hora_inicio}
                  onChange={handleChange}
                  required
                  data-testid="input-hora-inicio"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="hora_fim">Horário de Término *</Label>
                <Input
                  id="hora_fim"
                  name="hora_fim"
                  type="time"
                  value={formData.hora_fim}
                  onChange={handleChange}
                  required
                  data-testid="input-hora-fim"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="valor">Valor do Procedimento (R$)</Label>
              <Input
                id="valor"
                name="valor"
                type="text"
                inputMode="decimal"
                pattern="[0-9]*[.,]?[0-9]*"
                value={formData.valor}
                onChange={(e) => {
                  // Only allow numbers, dots and commas
                  const value = e.target.value.replace(/[^0-9.,]/g, '');
                  setFormData(prev => ({ ...prev, valor: value }));
                }}
                placeholder="Ex: 1500.00"
                data-testid="input-valor"
              />
            </div>

            <div className="space-y-2">
              <Label>Modo de Atendimento *</Label>
              <div className="flex gap-6">
                <label
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border-2 cursor-pointer transition-all ${
                    formData.modo_atendimento === 'presencial'
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                  }`}
                  data-testid="radio-presencial"
                >
                  <input
                    type="radio"
                    name="modo_atendimento"
                    value="presencial"
                    checked={formData.modo_atendimento === 'presencial'}
                    onChange={handleChange}
                    className="sr-only"
                    required={!formData.modo_atendimento}
                  />
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>
                  <span className="text-sm font-medium">Presencial</span>
                </label>
                <label
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border-2 cursor-pointer transition-all ${
                    formData.modo_atendimento === 'online'
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                  }`}
                  data-testid="radio-online"
                >
                  <input
                    type="radio"
                    name="modo_atendimento"
                    value="online"
                    checked={formData.modo_atendimento === 'online'}
                    onChange={handleChange}
                    className="sr-only"
                  />
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="15" x="2" y="3" rx="2"/><line x1="8" x2="16" y1="21" y2="21"/><line x1="12" x2="12" y1="18" y2="21"/></svg>
                  <span className="text-sm font-medium">Online</span>
                </label>
              </div>
              {!formData.modo_atendimento && (
                <p className="text-xs text-gray-400">Selecione o modo de atendimento</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="observacoes">Observações</Label>
              <textarea
                id="observacoes"
                name="observacoes"
                value={formData.observacoes}
                onChange={handleChange}
                rows={4}
                placeholder="Informações adicionais sobre a consulta..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
                data-testid="textarea-observacoes"
              />
            </div>

            <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/agenda')}
                className="w-full sm:w-auto"
                data-testid="btn-cancelar"
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={loading} className="w-full sm:w-auto" data-testid="btn-agendar">
                {loading ? 'Salvando...' : (editingConsulta ? 'Atualizar Consulta' : 'Agendar Consulta')}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </Layout>
  );
}

export default NovaConsultaPage;
